import * as THREE from 'three';
import { SceneHost } from './render/scene';
import { TrackMesh } from './render/trackMesh';
import { SupportMesh } from './render/supportMesh';
import { SupportFootEditor } from './render/supportFootEditor';
import { SupportStubEditor } from './render/supportStubEditor';
import { TrainMesh } from './render/trainMesh';
import { Overlays } from './render/overlays';
import { AppStore } from './state/store';
import { dirtySpansFor, autoSolveChecks } from './state/commands';
import { setDefaultHint, flashHint } from './ui/toolbar';
import { Inspector } from './ui/inspector';
import { TrackPanel } from './ui/trackPanel';
import { ScalePanel } from './ui/scalePanel';
import { GridPanel } from './ui/gridPanel';
import { TrainPanel } from './ui/trainPanel';
import { SupportPanel } from './ui/supportPanel';
import { TrackShapePanel } from './ui/trackShapePanel';
import { PrintPanel } from './ui/printPanel';
import { FilePanel } from './ui/filePanel';
import { enableSidebarResize } from './ui/sidebarResize';
import { enableSidebarTabs } from './ui/sidebarTabs';
import { enableGraphCollapse } from './ui/graphCollapse';
import { RollEditor } from './ui/rollEditor';
import { Editor } from './ui/editor';
import { FlyMode } from './ui/flyMode';
import { RctMode } from './ui/rctMode';
import { documentToFile, fileToDocument } from './core/doc/document';
import { getDefaultRide } from './presets/defaultRides';
import { generateSupportsInBackground } from './core/supports/generateAsync';
import { isSuppressed, suppressRangeForAttach } from './core/supports/generate';
import { Simulator } from './core/sim/physics';
import { carGForces } from './core/sim/forces';
import { validateTrack } from './core/track/validate';
import { PrintPreview } from './render/printPreview';
import { ChunkCube } from './render/chunkCube';
import { PrintJob } from './core/print/types';

// ── document bootstrap: autosave restore, else DefaultRides Goliath ─────────
const AUTOSAVE_KEY = 'rcsim-autosave';
const CAMERA_KEY = 'rcsim-camera';
let initialDoc = getDefaultRide();
let restoredName: string | null = null;
try {
  const saved = localStorage.getItem(AUTOSAVE_KEY);
  if (saved) {
    const doc = fileToDocument(JSON.parse(saved));
    if (doc.track.nodes.length > 1) {
      initialDoc = doc;
      restoredName = doc.meta.name;
    }
  }
} catch {
  // corrupt autosave — fall back to the default preset
}

const store = new AppStore(initialDoc);
const host = new SceneHost(document.getElementById('viewport') as HTMLCanvasElement);
restoreCamera();
const trackMesh = new TrackMesh();
const supportMesh = new SupportMesh();
const supportFeet = new SupportFootEditor(host);
const supportStubs = new SupportStubEditor(host);
const trainMesh = new TrainMesh();
const overlays = new Overlays();
const printPreview = new PrintPreview();
const chunkCube = new ChunkCube(host);
chunkCube.setChunkSizeMm(store.doc.print.chunkSizeMm);
let printJob: PrintJob | null = null;
host.scene.add(
  trackMesh.group,
  supportMesh.group,
  supportFeet.group,
  supportStubs.group,
  trainMesh.group,
  overlays.group,
  overlays.scrubGroup,
  printPreview.group,
  chunkCube.group,
);

let sim = new Simulator(store.runtime, store.doc.train, store.doc.settings);
/** Matches the Simulate checkbox — on by default so the train runs on first load. */
let simEnabled = true;
let simPlaying = true;
let simSpeed = 1;
let camMode: 'orbit' | 'pov' = 'orbit';

const editor = new Editor(store, host, overlays, () => [trackMesh.group]);
const flyMode = new FlyMode(store, host);
const rctMode = new RctMode(store, host);

const rollEditor = new RollEditor(store);
let scrubS: number | null = null;
rollEditor.onHoverS = (s) => {
  scrubS = s;
  overlays.setScrub(s === null ? null : store.runtime, s);
};
enableSidebarTabs();
enableSidebarResize();
enableGraphCollapse();
const inspector = new Inspector(store);
new TrackPanel(store);
new GridPanel({
  onChange: (prefs) => {
    host.setSnapGrid(prefs.enabled, prefs.size);
    editor.snapEnabled = prefs.enabled;
    editor.snapSize = prefs.size;
    editor.showAllCoords = prefs.showCoords;
  },
});
const supportPanel = new SupportPanel(store, {
  onDebugColors: (on) => {
    supportMesh.debugColors = on;
    regenSupports();
  },
  onSelectFeet: (sels) => {
    supportStubs.clearSelection();
    supportPanel.setSelectedStub(null);
    supportFeet.setSelections(sels);
  },
});
new TrackShapePanel(store);
new ScalePanel(store);
const printPanel = new PrintPanel(store, {
  onJobChanged: (job) => {
    printJob = job;
    printPreview.clearPartVisibility();
    printPreview.rebuild(job, store.runtime);
    syncEditorMeshesForPrintPreview();
  },
  onPreviewMode: (show, kind) => {
    printPreview.setPreviewKind(kind);
    printPreview.setVisible(show);
    if (show && printJob) printPreview.rebuild(printJob, store.runtime);
    syncEditorMeshesForPrintPreview();
  },
  onHighlightPart: (partId) => printPreview.setHighlight(partId),
  onPartVisible: (partId, visible) => printPreview.setPartVisible(partId, visible),
});
const trainPanel = new TrainPanel(store, {
  onSimEnabledChange: (enabled) => {
    simEnabled = enabled;
    simPlaying = enabled;
    document.getElementById('hud')!.classList.toggle('hidden', !enabled);
    if (enabled) {
      // Uncheck → check restarts from the station, not mid-circuit.
      sim.reset();
      sim.dispatch();
      updateSimProfile();
      rollEditor.draw();
    } else {
      rollEditor.simTrace.headS = -1;
      rollEditor.draw();
      camMode = 'orbit';
      host.setPovLookEnabled(false);
      host.orbit.enabled = true;
    }
    syncEditOverlayVisibility();
  },
  onCameraChange: (cam) => {
    camMode = cam;
    host.setPovLookEnabled(cam === 'pov');
    if (cam === 'orbit') host.orbit.enabled = true;
    syncEditOverlayVisibility();
  },
});
new FilePanel(store);

/** While print chunks are shown, hide the live track/supports so hidden chunks stay empty. */
function syncEditorMeshesForPrintPreview(): void {
  const replace = printPreview.visible && !!printJob;
  trackMesh.group.visible = !replace;
  supportMesh.group.visible = !replace;
  supportFeet.setEnabled(!replace && store.doc.supportsEnabled);
  supportStubs.setEnabled(!replace && store.doc.supportsEnabled);
}

function clearSupportSelections(): void {
  supportFeet.clearSelection();
  supportStubs.clearSelection();
  supportPanel.setSelectedFeet([]);
  supportPanel.setSelectedStub(null);
}

editor.earlyPointerDown = (e) => {
  const canvas = host.renderer.domElement;
  if (printPreview.visible) {
    const partId = printPreview.pickAtScreen(host.camera, e.clientX, e.clientY, canvas);
    if (partId) {
      chunkCube.setSelected(false);
      clearSupportSelections();
      printPanel.selectPart(partId, { scroll: true });
      return true;
    }
  }
  // Once selected, never steal pointerdown from TransformControls — the cube mesh
  // sits under the arrows and would otherwise block every drag.
  if (chunkCube.isSelected) {
    if (
      chunkCube.hitsGizmo(host.camera, e.clientX, e.clientY, canvas) ||
      chunkCube.pickAtScreen(host.camera, e.clientX, e.clientY, canvas)
    ) {
      return 'passthrough-hit';
    }
    return false;
  }
  if (supportFeet.selections.length > 0) {
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    // Ctrl/Shift+click adds more pads to the merge set — skip the translate gizmo
    // whose large pickers otherwise steal the click.
    if (!additive && supportFeet.hitsGizmo(host.camera, e.clientX, e.clientY, canvas)) {
      return 'passthrough-hit';
    }
    const hitFoot = supportFeet.pickAtScreen(host.camera, e.clientX, e.clientY, canvas);
    if (hitFoot) {
      supportStubs.clearSelection();
      supportPanel.setSelectedStub(null);
      if (additive) {
        supportFeet.toggleInSelection(hitFoot);
        supportPanel.setSelectedFeet(supportFeet.selections);
        return true;
      }
      const only =
        supportFeet.selections.length === 1 &&
        supportFeet.selections[0].role === hitFoot.role &&
        Math.abs(supportFeet.selections[0].attachS - hitFoot.attachS) < 1e-4;
      supportFeet.setSelection(hitFoot);
      supportPanel.setSelectedFeet([hitFoot]);
      return only ? 'passthrough-hit' : true;
    }
    if (supportFeet.hitsGizmo(host.camera, e.clientX, e.clientY, canvas)) {
      return 'passthrough-hit';
    }
  }
  if (chunkCube.pickAtScreen(host.camera, e.clientX, e.clientY, canvas)) {
    store.setSelection([]);
    clearSupportSelections();
    chunkCube.setSelected(true);
    return true;
  }
  const stubS = supportStubs.pickAtScreen(host.camera, e.clientX, e.clientY, canvas);
  if (stubS != null) {
    store.setSelection([]);
    chunkCube.setSelected(false);
    supportFeet.clearSelection();
    supportPanel.setSelectedFeet([]);
    supportStubs.setSelection(stubS);
    supportPanel.setSelectedStub(stubS);
    return true;
  }
  const foot = supportFeet.pickAtScreen(host.camera, e.clientX, e.clientY, canvas);
  if (foot) {
    store.setSelection([]);
    chunkCube.setSelected(false);
    supportStubs.clearSelection();
    supportPanel.setSelectedStub(null);
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    if (additive) {
      supportFeet.toggleInSelection(foot);
    } else {
      supportFeet.setSelection(foot);
    }
    supportPanel.setSelectedFeet(supportFeet.selections);
    return true;
  }
  return false;
};
editor.onViewportSelect = () => {
  if (chunkCube.isDragging() || supportFeet.isDragging()) return;
  chunkCube.setSelected(false);
  clearSupportSelections();
};

editor.earlyKeyDown = (e) => {
  if (e.key.toLowerCase() === 'h' && !e.ctrlKey && !e.metaKey && !e.altKey) {
    return printPanel.toggleSelectedPartVisibility();
  }
  if (e.key !== 'Delete' && e.key !== 'Backspace') return false;
  if (store.mode !== 'build') return false;
  const attachS = supportStubs.selection;
  if (attachS == null) return false;
  if (isSuppressed(attachS, store.doc.supportOverrides)) {
    supportStubs.clearSelection();
    supportPanel.setSelectedStub(null);
    flashHint('Support already suppressed');
    return true;
  }
  const range = suppressRangeForAttach(
    attachS,
    store.runtime.totalLength,
    store.doc.supports.supportCount,
  );
  store.commit((d) => {
    d.supportOverrides.push({ kind: 'suppress', from: range.from, to: range.to });
  });
  supportStubs.clearSelection();
  supportPanel.setSelectedStub(null);
  store.emit('supports');
  flashHint(`Suppressed support @ s=${attachS.toFixed(1)} m`);
  return true;
};

supportFeet.onSelect = (sels) => {
  if (sels.length > 0) {
    supportStubs.clearSelection();
    supportPanel.setSelectedStub(null);
  }
  supportPanel.setSelectedFeet(sels);
};
supportStubs.onSelect = (attachS) => {
  if (attachS != null) {
    supportFeet.clearSelection();
    supportPanel.setSelectedFeet([]);
  }
  supportPanel.setSelectedStub(attachS);
};
supportFeet.onFootMoveStart = () => store.beginGesture();
supportFeet.onFootMove = (sel, x, z) => {
  const overrides = store.doc.supportOverrides;
  const idx = overrides.findIndex(
    (o) =>
      o.kind === 'forceFoot' &&
      Math.abs(o.s - sel.attachS) < 1e-4 &&
      (o.role ?? 'main') === sel.role,
  );
  const next = { kind: 'forceFoot' as const, s: sel.attachS, x, z, role: sel.role };
  if (idx >= 0) overrides[idx] = next;
  else overrides.push(next);
  supportPanel.setSelectedFeet([sel]);
  store.emit('supports');
};
supportFeet.onFootMoveEnd = () => store.endGesture();

editor.setTool('select');
document.getElementById('hud')!.classList.toggle('hidden', !simEnabled);
setDefaultHint(
  'WASD pan · Space up · Shift down · drag to orbit · double-click track to insert · Shift/Ctrl-click multi-select',
);
if (restoredName) flashHint(`Restored autosaved coaster "${restoredName}"`);

inspector.onWarningClick = (s) => {
  const f = store.runtime.frameAt(s);
  host.orbit.target.copy(f.pos);
  host.camera.position.copy(f.pos.clone().add(new THREE.Vector3(25, 18, 25)));
  trackMesh.blinkAtS(store.runtime, s);
};
inspector.onAutoSolve = () => {
  const remaining = autoSolveChecks(store);
  const rb = store.doc.track.closed ? sim.rollbackCheck() : null;
  inspector.setWarnings(remaining, rb);
  const unsolved = remaining.filter(
    (w) => w.kind === 'curvature-step' || w.kind === 'roll-rate' || w.kind === 'below-ground',
  );
  if (unsolved.length === 0) flashHint('Checks auto-solved');
  else flashHint(`Auto-solve applied — ${unsolved.length} check(s) left`);
};

// ── derived-state updates ───────────────────────────────────────────────────

function fullRebuild(): void {
  trackMesh.setTrackShape(store.doc.trackShape);
  trackMesh.rebuildAll(store.runtime);
  overlays.syncNodes(store.doc.track, store.selection);
  overlays.updateComb(store.runtime);
  overlays.updateSections(store.runtime, store.doc.sections);
  overlays.setScrub(scrubS === null ? null : store.runtime, scrubS);
  scheduleSupports();
  scheduleValidation();
  scheduleAutosave();
}

store.on('track', (dirtyNodes) => {
  trackMesh.setTrackShape(store.doc.trackShape);
  if (dirtyNodes && dirtyNodes.length > 0) {
    trackMesh.rebuildSpans(store.runtime, dirtySpansFor(store, dirtyNodes));
  } else {
    trackMesh.rebuildAll(store.runtime);
  }
  overlays.syncNodes(store.doc.track, store.selection);
  overlays.updateComb(store.runtime);
  overlays.updateSections(store.runtime, store.doc.sections);
  overlays.setScrub(scrubS === null ? null : store.runtime, scrubS);
  editor.refreshAttachment();
  recreateSim();
  scheduleSupports();
  scheduleValidation();
  scheduleAutosave();
  chunkCube.setChunkSizeMm(store.doc.print.chunkSizeMm);
});

store.on('doc', () => {
  fullRebuild();
  recreateSim();
  chunkCube.setChunkSizeMm(store.doc.print.chunkSizeMm);
});

store.on('selection', () => {
  overlays.syncNodes(store.doc.track, store.selection);
  editor.refreshAttachment();
});

store.on('train', () => recreateSim());

store.on('supports', () => regenSupports());

store.on('mode', () => {
  chunkCube.setVisible(store.mode === 'build');
  if (store.mode === 'simulate') {
    flyMode.setEnabled(false);
    rctMode.setEnabled(false);
    recreateSim();
    updateSimProfile();
  } else {
    trainPanel.setViewTrain(false);
    if (editor.tool === 'fly') flyMode.setEnabled(true);
    else if (editor.tool === 'rct') rctMode.setEnabled(true);
    else host.orbit.enabled = true;
  }
  syncEditOverlayVisibility();
});

/** Hide node spheres / XYZ tips while riding in the seat camera. */
function syncEditOverlayVisibility(): void {
  const riding = camMode === 'pov';
  overlays.group.visible = store.mode === 'build' && !riding;
  editor.suppressCoordTips = riding;
  if (riding) editor.updateCoordLabels();
}

function recreateSim(): void {
  const wasPlaying = simPlaying;
  const oldS = sim.s;
  const oldV = sim.v;
  const oldPhase = sim.phase;
  const oldDwell = sim.dwellRemaining;
  sim = new Simulator(store.runtime, store.doc.train, store.doc.settings);
  if (oldPhase !== 'stopped') {
    sim.s = Math.min(oldS, Math.max(store.runtime.totalLength - 0.1, 0));
    sim.v = oldV;
    sim.phase = oldPhase;
    if (oldPhase === 'waiting') sim.dwellRemaining = oldDwell;
  }
  trainMesh.setTrain(store.doc.train);
  simPlaying = wasPlaying;
  if (simEnabled) {
    // Initial start only — station dwell/re-dispatch is handled inside the sim.
    if (sim.phase === 'stopped') sim.dispatch();
    updateSimProfile();
  }
}

/** Speed/G trace for the graph — an energy march once around with the min-speed floor. */
function updateSimProfile(): void {
  if (!store.doc.track.closed) {
    rollEditor.simTrace.speedProfile = null;
    return;
  }
  const station = store.doc.sections.find((s) => s.kind === 'station');
  const start = station
    ? store.runtime.centerline.normalizeS(station.to + store.doc.train.cars * store.doc.train.carLength)
    : 0;
  const minV = Math.max(store.doc.settings.minSpeed, 1);
  const profile: { s: number; v: number; gVert: number }[] = [];
  let v2 = minV * minV;
  const L = store.runtime.totalLength;
  for (let d = 0; d < L; d += 2) {
    const s = store.runtime.centerline.normalizeS(start + d);
    const v = Math.sqrt(Math.max(v2, minV * minV));
    const a = sim.accel(s, v);
    const g = carGForces(store.runtime, { ...store.doc.train, cars: 1 }, s, v, a, store.doc.settings.gravity)[0];
    profile.push({ s, v, gVert: g.vertical });
    v2 = Math.max(v2 + 2 * a * 2, minV * minV);
  }
  profile.sort((a, b) => a.s - b.s);
  rollEditor.simTrace.speedProfile = profile;
  rollEditor.draw();
}

// supports on a debounce — the track follows the cursor, the supports need not.
// Generation runs in a Web Worker so the sim/render loop stays responsive.
let supportsTimer = 0;
let supportsEpoch = 0;

function invalidateSupportsJobs(): void {
  supportsEpoch += 1;
}

function scheduleSupports(): void {
  window.clearTimeout(supportsTimer);
  invalidateSupportsJobs();
  const epoch = supportsEpoch;
  supportsTimer = window.setTimeout(() => {
    if (epoch !== supportsEpoch) return;
    void runSupports(epoch);
  }, 350);
}

function regenSupports(): void {
  window.clearTimeout(supportsTimer);
  invalidateSupportsJobs();
  void runSupports(supportsEpoch);
}

async function runSupports(epoch: number): Promise<void> {
  if (!store.doc.supportsEnabled) {
    if (epoch !== supportsEpoch) return;
    supportMesh.rebuild({ primitives: [], feet: [], skipped: [], warnings: [] });
    supportFeet.syncFeet([]);
    supportStubs.syncStubs([]);
    supportPanel.setFeet([]);
    supportFeet.setEnabled(false);
    supportStubs.setEnabled(false);
    return;
  }

  const snapshot = documentToFile(store.doc);
  try {
    const result = await generateSupportsInBackground(snapshot);
    if (epoch !== supportsEpoch) return;
    supportMesh.rebuild(result);
    const pickable = !printPreview.visible || !printJob;
    supportFeet.setEnabled(pickable);
    supportStubs.setEnabled(pickable);
    supportFeet.syncFeet(result.feet);
    supportStubs.syncStubs(result.primitives);
    supportPanel.setFeet(result.feet);
    if (result.warnings.length > 0) {
      flashHint(result.warnings[0]);
    }
  } catch (err) {
    if (epoch !== supportsEpoch) return;
    console.error(err);
    flashHint('Support generation failed');
  }
}

let validateTimer = 0;
function scheduleValidation(): void {
  window.clearTimeout(validateTimer);
  validateTimer = window.setTimeout(() => {
    const warnings = validateTrack(store.runtime.centerline, store.doc.rollKeys);
    const rb = store.doc.track.closed ? sim.rollbackCheck() : null;
    inspector.setWarnings(warnings, rb);
  }, 400);
}

let autosaveTimer = 0;
function scheduleAutosave(): void {
  window.clearTimeout(autosaveTimer);
  autosaveTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(documentToFile(store.doc)));
    } catch {
      // storage full/blocked — autosave is best-effort
    }
  }, 1200);
}

// ── camera persistence — the orbit pose survives a reload ───────────────────
function restoreCamera(): void {
  try {
    const raw = localStorage.getItem(CAMERA_KEY);
    if (!raw) return;
    const c = JSON.parse(raw) as { pos: number[]; target: number[] };
    if (!Array.isArray(c?.pos) || !Array.isArray(c?.target)) return;
    if (![...c.pos, ...c.target].every((n) => typeof n === 'number' && Number.isFinite(n))) return;
    host.camera.position.fromArray(c.pos);
    host.orbit.target.fromArray(c.target);
    host.orbit.update();
  } catch {
    // corrupt entry — keep the default framing
  }
}

let cameraTimer = 0;
function saveCamera(): void {
  // POV drives the camera directly; only the orbit pose is worth remembering
  if (camMode !== 'orbit') return;
  try {
    localStorage.setItem(
      CAMERA_KEY,
      JSON.stringify({ pos: host.camera.position.toArray(), target: host.orbit.target.toArray() }),
    );
  } catch {
    // storage full/blocked — best-effort
  }
}

host.orbit.addEventListener('change', () => {
  window.clearTimeout(cameraTimer);
  cameraTimer = window.setTimeout(saveCamera, 400);
});
window.addEventListener('pagehide', saveCamera);

// ── HUD ─────────────────────────────────────────────────────────────────────
const hudEl = document.getElementById('hud')!;
function updateHud(): void {
  if (!simEnabled) return;
  const gs = carGForces(store.runtime, store.doc.train, sim.s, sim.v, sim.accel(sim.s, sim.v), store.doc.settings.gravity);
  const front = gs[0] ?? { vertical: 1, lateral: 0, longitudinal: 0 };
  const back = gs[gs.length - 1] ?? front;
  const h = store.runtime.centerline.posAt(sim.s).y;
  hudEl.innerHTML = `
    <div class="big">${(sim.v * 3.6).toFixed(0)} km/h</div>
    <div>${sim.v.toFixed(1)} m/s · h ${h.toFixed(1)} m · t ${sim.time.toFixed(0)} s</div>
    <div class="gbar"><span>front</span> ${front.vertical.toFixed(1)}G vert · ${front.lateral.toFixed(1)}G lat</div>
    <div class="gbar"><span>back</span> ${back.vertical.toFixed(1)}G vert · ${back.lateral.toFixed(1)}G lat</div>
    <div class="gbar"><span>phase</span> ${sim.phase}</div>`;
}

// ── render loop ─────────────────────────────────────────────────────────────
let lastT = performance.now();
let hudCounter = 0;
function tick(now: number): void {
  const dt = Math.min((now - lastT) / 1000, 0.1);
  lastT = now;

  if (simEnabled && simPlaying) {
    // First start from the platform; after a lap the sim dwells then re-dispatches.
    if (sim.phase === 'stopped') sim.dispatch();
    sim.update(dt, simSpeed);
    rollEditor.simTrace.headS = sim.s;
    if (++hudCounter % 3 === 0) rollEditor.draw();
  }
  if (simEnabled) {
    trainMesh.update(store.runtime, sim.s, sim.v, dt);
    trainMesh.group.visible = true;
    if (hudCounter % 3 === 0) updateHud();
    hudCounter++;

    if (camMode === 'pov') {
      const pose = trainMesh.povPose();
      if (pose) {
        host.orbit.enabled = false;
        host.applyPovCamera(pose.pos, pose.quaternion);
      }
    } else {
      host.camera.up.set(0, 1, 0);
    }
  } else {
    trainMesh.group.visible = false;
    if (host.orbitKeepWorldUp) host.camera.up.set(0, 1, 0);
  }

  flyMode.update(dt);
  host.updateOrbitWasd(dt);
  trackMesh.updateBlink(now);

  if (host.orbit.enabled) host.orbit.update();
  editor.updateCoordLabels();
  host.render();
  requestAnimationFrame(tick);
}

fullRebuild();
recreateSim();
requestAnimationFrame(tick);

// dev/debug handle (harmless in production)
(window as any).__rcsim = {
  store,
  host,
  editor,
  overlays,
  /** Bump when node-picking changes — check in Chrome/Edge console after hard refresh. */
  pickVersion: 3,
  getSim: () => sim,
  setCam(px: number, py: number, pz: number, tx: number, ty: number, tz: number) {
    host.camera.position.set(px, py, pz);
    host.orbit.target.set(tx, ty, tz);
    host.orbit.update();
  },
};

// Vite HMR of editor/overlays previously stacked pointer listeners + TransformControls
// that cleared selection on pointerup in Chrome/Edge tabs left open during edits.
if (import.meta.hot) {
  import.meta.hot.accept(['./ui/editor.ts', './render/overlays.ts'], () => {
    window.location.reload();
  });
}
